/**
 * BootstrapUI - 基于 Bootstrap 的 UI 组件创建和事件管理类
 * 提供统一的 DOM 元素创建、事件绑定和组件构建功能
 */

import { SelectUI, SelectOptions as SelectUIOptions } from './ui/selectUI';
import { SelectionListUI, SelectionListOptions } from './ui/selectionListUI';
import { NumericInputUI, NumericInputOptions } from './ui/numericInputUI';
import { NumericSliderUI, NumericSliderOptions } from './ui/numericSliderUI';
import { ThemeSelectorUI, ThemeSelectorOptions } from './ui/themeSelectorUI';
import { DropdownTabUI, DropdownTabOptions } from './ui/dropdownTabUI';
import { CheckButtonGroupUI, CheckButtonGroupOptions } from './ui/checkButtonGroupUI';
import { getI18nText } from '@/utils/commonFunc';

export interface ElementOptions {
  tag?: string;
  id?: string;
  className?: string | string[];
  attributes?: Record<string, string>;
  textContent?: string;
  innerHTML?: string;
  children?: HTMLElement[];
  styles?: Record<string, string>;
  data?: Record<string, string>;
  i18nKey?: string;
  iconName?: string;
  isIconOnly?: boolean;
}

export interface ButtonOptions extends ElementOptions {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'outline-primary' | 'outline-secondary' | 'outline-success' | 'outline-danger' | 'outline-warning' | 'outline-info' | 'outline-light' | 'outline-dark';
  size?: 'sm' | 'lg';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  iconName?: string;
  isIconOnly?: boolean;
  i18nKey?: string;
}

// export interface ModalOptions extends ElementOptions {
//   title?: string;
//   body?: string | HTMLElement;
//   footer?: HTMLElement[];
//   size?: 'sm' | 'lg' | 'xl';
//   backdrop?: boolean | 'static';
//   keyboard?: boolean;
//   focus?: boolean;
// }

// export interface CardOptions extends ElementOptions {
//   title?: string;
//   subtitle?: string;
//   text?: string;
//   image?: {
//     src: string;
//     alt: string;
//     position?: 'top' | 'bottom';
//   };
//   header?: HTMLElement;
//   footer?: HTMLElement;
// }

export interface IconOptions extends ElementOptions {
  iconName: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  color?: string;
  spin?: boolean;
  pulse?: boolean;
}

// export interface FormOptions extends ElementOptions {
//   method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
//   action?: string;
//   enctype?: string;
// }

// export interface InputOptions extends ElementOptions {
//   type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local' | 'color' | 'range' | 'file';
//   placeholder?: string;
//   value?: string;
//   required?: boolean;
//   disabled?: boolean;
//   readonly?: boolean;
//   min?: number | string;
//   max?: number | string;
//   step?: number | string;
//   pattern?: string;
//   label?: string;
//   help?: string;
//   invalid?: boolean;
//   valid?: boolean;
// }

export interface SelectOption {
  /** 选项值 */
  value: string;
  /** 显示文本 */
  text: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否选中 */
  selected?: boolean;
  /** 多语言键 */
  i18nKey?: string;
}

export interface SelectOptions extends ElementOptions {
  /** 选项列表 */
  options?: SelectOption[];
  /** 占位符文本 */
  placeholder?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否多选 */
  multiple?: boolean;
  /** 是否必选 */
  required?: boolean;
  /** 下拉框大小 */
  size?: 'sm' | 'lg';
  /** 标签文本 */
  label?: string;
  /** 帮助文本 */
  help?: string;
  /** 是否无效 */
  invalid?: boolean;
  /** 是否有效 */
  valid?: boolean;
  /** 多语言键 */
  i18nKey?: string;
}

export interface LabelOptions extends ElementOptions {
  /** 关联的输入元素ID */
  for?: string;
  /** 标签文本 */
  text?: string;
  /** 标签大小 */
  size?: 'sm' | 'lg';
  /** 是否必填标识 */
  required?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 多语言键 */
  i18nKey?: string;
}


export class BootstrapUI {
  private eventDelegates: Map<string, { selector: string; handler: EventListener }> = new Map();

  /**
   * 创建 DOM 元素
   * @param options 元素配置选项
   * @returns 创建的 HTMLElement
   */
  createElement(options: ElementOptions): HTMLElement {
    const {
      tag = 'div',
      id,
      className,
      attributes = {},
      innerHTML,
      children = [],
      styles = {},
      data = {},
      i18nKey,
      iconName,
      isIconOnly = false,
      ...elementOptions
    } = options;

    let textContent = options.textContent;
    const element = document.createElement(tag);

    // 设置 ID
    if (id) {
      element.id = id;
    }
    // 设置多语言
    if (i18nKey) {
      if (textContent) {
        textContent = getI18nText(i18nKey);
      }
      element.setAttribute('data-i18n', i18nKey);
    }

    // 设置类名
    if (className) {
      if (Array.isArray(className)) {
        // 过滤掉空字符串和无效的类名
        const validClassNames = className.filter(cls => cls && typeof cls === 'string' && cls.trim().length > 0);
        if (validClassNames.length > 0) {
          element.classList.add(...validClassNames);
        }
      } else {
        // 处理包含空格的 className 字符串
        const classNames = className.trim().split(/\s+/).filter(cls => cls.length > 0);
        element.classList.add(...classNames);
      }
    }

    // 设置属性
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });

    // 设置数据属性
    Object.entries(data).forEach(([key, value]) => {
      element.setAttribute(`data-${key}`, value);
    });

    // 设置样式
    Object.entries(styles).forEach(([property, value]) => {
      element.style.setProperty(property, value);
    });

    // 创建图标组件（如果提供了 iconName）
    if (iconName) {
      const iconElement = this.createIcon({
        iconName,
        // className: options.isIconOnly ? ['me-0','inherit-font-size'] : ['me-2']
        className: options.isIconOnly ? ['inherit-font-size'] : ['me-2']
      });
      element.appendChild(iconElement);
    }

    // 设置内容（如果 isIconOnly 为 false 或者没有图标）
    if (!options.isIconOnly) {
      if (textContent) {
        element.textContent = textContent;
      } else if (innerHTML) {
        element.innerHTML = innerHTML;
      }
    }

    // 添加子元素
    children.forEach(child => {
      element.appendChild(child);
    });

    return element;
  }

  /**
   * 事件绑定方法
   * @param event 事件类型
   * @param element 目标元素或选择器
   * @param handler 事件处理函数
   * @param useCapture 是否使用捕获阶段
   */
  on(event: string, element: string | HTMLElement, handler: EventListener, useCapture: boolean = false): void {
    if (typeof element === 'string') {
      // 事件委托模式
      const delegateId = `delegate_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.eventDelegates.set(delegateId, { selector: element, handler });

      document.addEventListener(event, (e) => {
        const target = e.target as HTMLElement;
        if (target && target.matches && target.matches(element)) {
          handler.call(target, e);
        }
      }, useCapture);
    } else {
      // 直接绑定模式
      element.addEventListener(event, handler, useCapture);
    }
  }

  /**
   * 移除事件监听器
   * @param event 事件类型
   * @param element 目标元素
   * @param handler 事件处理函数
   */
  off(event: string, element: HTMLElement, handler: EventListener): void {
    element.removeEventListener(event, handler);
  }

  /**
   * 创建 Bootstrap 按钮
   * @param options 按钮配置选项
   * @returns 创建的按钮元素
   */
  createButton(options: ButtonOptions = {}): HTMLButtonElement {
    const {
      variant = '',
      size,
      disabled = false,
      type = 'button',
      iconName,
      className = [],
      isIconOnly = false,
      ...elementOptions
    } = options;

    const buttonClasses = ['btn', `btn-${variant}`];
    if (size) {
      buttonClasses.push(`btn-${size}`);
    }
    if (Array.isArray(className)) {
      buttonClasses.push(...className);
    } else if (className) {
      buttonClasses.push(className);
    }

    const button = this.createElement({
      ...elementOptions,
      tag: 'button',
      className: buttonClasses,
      iconName: iconName,
      isIconOnly: options.isIconOnly,
      attributes: {
        type,
        ...(disabled && { disabled: 'true' })
      }
    }) as HTMLButtonElement;

    return button;
  }

  /**
   * 创建 Bootstrap 标签
   * @param options 标签配置选项
   * @returns 创建的标签元素
   */
  createLabel(options: LabelOptions = {}): HTMLLabelElement {
    const {
      for: forAttr,
      text,
      size,
      required = false,
      disabled = false,
      className = [],
      ...elementOptions
    } = options;

    const labelClasses = ['form-label'];
    if (size) {
      labelClasses.push(`form-label-${size}`);
    }
    if (Array.isArray(className)) {
      labelClasses.push(...className);
    } else if (className) {
      labelClasses.push(className);
    }

    const label = this.createElement({
      ...elementOptions,
      tag: 'label',
      className: labelClasses,
      textContent: text,
      attributes: {
        ...(forAttr && { for: forAttr }),
        ...(disabled && { 'aria-disabled': 'true' })
      }
    }) as HTMLLabelElement;

    // 添加必填标识
    if (required) {
      const requiredSpan = this.createElement({
        tag: 'span',
        className: 'text-danger',
        textContent: ' *'
      });
      label.appendChild(requiredSpan);
    }

    return label;
  }

  /**
   * 创建 Bootstrap 下拉选择框
   * @param options 下拉框配置选项
   * @returns 创建的下拉框元素
   */
  createSelect(options: SelectOptions = {}): HTMLSelectElement {
    const {
      options: selectOptions = [],
      placeholder,
      disabled = false,
      multiple = false,
      required = false,
      size,
      label,
      help,
      invalid = false,
      valid = false,
      className = [],
      ...elementOptions
    } = options;

    const selectClasses = ['form-select'];
    if (size) {
      selectClasses.push(`form-select-${size}`);
    }
    if (invalid) {
      selectClasses.push('is-invalid');
    }
    if (valid) {
      selectClasses.push('is-valid');
    }
    if (Array.isArray(className)) {
      selectClasses.push(...className);
    } else if (className) {
      selectClasses.push(className);
    }

    const select = this.createElement({
      ...elementOptions,
      tag: 'select',
      className: selectClasses,
      attributes: {
        ...(disabled && { disabled: 'true' }),
        ...(multiple && { multiple: 'true' }),
        ...(required && { required: 'true' })
      }
    }) as HTMLSelectElement;

    // 添加占位符选项（仅单选模式且无默认选中项时）
    if (!multiple && placeholder && !selectOptions.some(option => option.selected)) {
      const placeholderOption = this.createElement({
        tag: 'option',
        textContent: placeholder,
        attributes: {
          value: '',
          disabled: 'true',
          selected: 'true'
        }
      }) as HTMLOptionElement;
      select.appendChild(placeholderOption);
    }

    // 添加选项
    selectOptions.forEach(option => {
      const optionElement = this.createElement({
        tag: 'option',
        textContent: option.text,
        attributes: {
          value: option.value,
          ...(option.disabled && { disabled: 'true' }),
          ...(option.selected && { selected: 'true' })
        }
      }) as HTMLOptionElement;
      select.appendChild(optionElement);
    });

    return select;
  }

  /**
   * 创建 SelectUI 组件实例
   * @param options SelectUI 配置选项
   * @returns 创建的 SelectUI 实例
   */
  createSelectUI(options: SelectUIOptions = {}): SelectUI {
    return new SelectUI(options);
  }

  /**
   * 创建 SelectionListUI 组件实例
   * @param options SelectionListUI 配置选项
   * @returns 创建的 SelectionListUI 实例
   */
  createSelectionListUI(options: SelectionListOptions = {}): SelectionListUI {
    return new SelectionListUI(options);
  }

  /**
   * 创建 NumericInputUI 组件实例
   * @param options NumericInputUI 配置选项
   * @returns 创建的 NumericInputUI 实例
   */
  createNumericInputUI(options: NumericInputOptions = {}): NumericInputUI {
    return new NumericInputUI(options);
  }

  /**
   * 创建 NumericSliderUI 组件实例
   * @param options NumericSliderUI 配置选项
   * @returns 创建的 NumericSliderUI 实例
   */
  createNumericSliderUI(options: NumericSliderOptions = {}): NumericSliderUI {
    return new NumericSliderUI(options);
  }

  /**
   * 创建 Bootstrap 模态框
   * @param options 模态框配置选项
   * @returns 创建的模态框元素
   */
  // createModal(options: ModalOptions = {}): HTMLElement {
  //   const {
  //     title,
  //     body,
  //     footer = [],
  //     size,
  //     backdrop = true,
  //     keyboard = true,
  //     focus = true,
  //     className = [],
  //     ...elementOptions
  //   } = options;

  //   const modalClasses = ['modal', 'fade'];
  //   if (Array.isArray(className)) {
  //     modalClasses.push(...className);
  //   } else if (className) {
  //     modalClasses.push(className);
  //   }

  //   const modal = this.createElement({
  //     ...elementOptions,
  //     className: modalClasses,
  //     attributes: {
  //       tabindex: '-1',
  //       'aria-hidden': 'true'
  //     }
  //   });

  //   const modalDialogClasses = ['modal-dialog'];
  //   if (size) {
  //     modalDialogClasses.push(`modal-${size}`);
  //   }

  //   const modalDialog = this.createElement({
  //     className: modalDialogClasses
  //   });

  //   const modalContent = this.createElement({
  //     className: 'modal-content'
  //   });

  //   // 模态框头部
  //   if (title) {
  //     const modalHeader = this.createElement({
  //       className: 'modal-header'
  //     });

  //     const modalTitle = this.createElement({
  //       tag: 'h5',
  //       className: 'modal-title',
  //       textContent: title
  //     });

  //     const closeButton = this.createElement({
  //       tag: 'button',
  //       className: ['btn-close'],
  //       attributes: {
  //         type: 'button',
  //         'data-bs-dismiss': 'modal',
  //         'aria-label': 'Close'
  //       }
  //     });

  //     modalHeader.appendChild(modalTitle);
  //     modalHeader.appendChild(closeButton);
  //     modalContent.appendChild(modalHeader);
  //   }

  //   // 模态框主体
  //   const modalBody = this.createElement({
  //     className: 'modal-body'
  //   });

  //   if (typeof body === 'string') {
  //     modalBody.innerHTML = body;
  //   } else if (body) {
  //     modalBody.appendChild(body);
  //   }

  //   modalContent.appendChild(modalBody);

  //   // 模态框底部
  //   if (footer.length > 0) {
  //     const modalFooter = this.createElement({
  //       className: 'modal-footer'
  //     });

  //     footer.forEach(footerElement => {
  //       modalFooter.appendChild(footerElement);
  //     });

  //     modalContent.appendChild(modalFooter);
  //   }

  //   modalDialog.appendChild(modalContent);
  //   modal.appendChild(modalDialog);

  //   return modal;
  // }

  /**
   * 创建 Bootstrap 卡片
   * @param options 卡片配置选项
   * @returns 创建的卡片元素
   */
  // createCard(options: CardOptions = {}): HTMLElement {
  //   const {
  //     title,
  //     subtitle,
  //     text,
  //     image,
  //     header,
  //     footer,
  //     className = [],
  //     ...elementOptions
  //   } = options;

  //   const cardClasses = ['card'];
  //   if (Array.isArray(className)) {
  //     cardClasses.push(...className);
  //   } else if (className) {
  //     cardClasses.push(className);
  //   }

  //   const card = this.createElement({
  //     ...elementOptions,
  //     className: cardClasses
  //   });

  //   // 卡片图片
  //   if (image && image.position === 'top') {
  //     const cardImg = this.createElement({
  //       tag: 'img',
  //       className: 'card-img-top',
  //       attributes: {
  //         src: image.src,
  //         alt: image.alt
  //       }
  //     });
  //     card.appendChild(cardImg);
  //   }

  //   // 卡片头部
  //   if (header) {
  //     const cardHeader = this.createElement({
  //       className: 'card-header'
  //     });
  //     cardHeader.appendChild(header);
  //     card.appendChild(cardHeader);
  //   }

  //   // 卡片主体
  //   const cardBody = this.createElement({
  //     className: 'card-body'
  //   });

  //   if (title) {
  //     const cardTitle = this.createElement({
  //       tag: 'h5',
  //       className: 'card-title',
  //       textContent: title
  //     });
  //     cardBody.appendChild(cardTitle);
  //   }

  //   if (subtitle) {
  //     const cardSubtitle = this.createElement({
  //       tag: 'h6',
  //       className: 'card-subtitle mb-2 text-muted',
  //       textContent: subtitle
  //     });
  //     cardBody.appendChild(cardSubtitle);
  //   }

  //   if (text) {
  //     const cardText = this.createElement({
  //       tag: 'p',
  //       className: 'card-text',
  //       textContent: text
  //     });
  //     cardBody.appendChild(cardText);
  //   }

  //   card.appendChild(cardBody);

  //   // 卡片底部
  //   if (footer) {
  //     const cardFooter = this.createElement({
  //       className: 'card-footer'
  //     });
  //     cardFooter.appendChild(footer);
  //     card.appendChild(cardFooter);
  //   }

  //   // 卡片图片（底部）
  //   if (image && image.position === 'bottom') {
  //     const cardImg = this.createElement({
  //       tag: 'img',
  //       className: 'card-img-bottom',
  //       attributes: {
  //         src: image.src,
  //         alt: image.alt
  //       }
  //     });
  //     card.appendChild(cardImg);
  //   }

  //   return card;
  // }

  /**
   * 创建 Bootstrap 表单
   * @param options 表单配置选项
   * @returns 创建的表单元素
   */
  // createForm(options: FormOptions = {}): HTMLFormElement {
  //   const {
  //     method = 'GET',
  //     action,
  //     enctype,
  //     className = [],
  //     ...elementOptions
  //   } = options;

  //   const formClasses = ['needs-validation'];
  //   if (Array.isArray(className)) {
  //     formClasses.push(...className);
  //   } else if (className) {
  //     formClasses.push(className);
  //   }

  //   const form = this.createElement({
  //     ...elementOptions,
  //     tag: 'form',
  //     className: formClasses,
  //     attributes: {
  //       method,
  //       ...(action && { action }),
  //       ...(enctype && { enctype })
  //     }
  //   }) as HTMLFormElement;

  //   return form;
  // }

  /**
   * 创建 Bootstrap 输入框
   * @param options 输入框配置选项
   * @returns 包含标签和输入框的容器元素
   */
  // createInput(options: InputOptions = {}): HTMLElement {
  //   const {
  //     type = 'text',
  //     placeholder,
  //     value,
  //     required = false,
  //     disabled = false,
  //     readonly = false,
  //     min,
  //     max,
  //     step,
  //     pattern,
  //     label,
  //     help,
  //     invalid = false,
  //     valid = false,
  //     className = [],
  //     ...elementOptions
  //   } = options;

  //   const container = this.createElement({
  //     className: 'mb-3'
  //   });

  //   // 标签
  //   if (label) {
  //     const labelElement = this.createElement({
  //       tag: 'label',
  //       className: 'form-label',
  //       textContent: label,
  //       attributes: {
  //         for: elementOptions.id || ''
  //       }
  //     });
  //     container.appendChild(labelElement);
  //   }

  //   // 输入框
  //   const inputClasses = ['form-control'];
  //   if (invalid) {
  //     inputClasses.push('is-invalid');
  //   } else if (valid) {
  //     inputClasses.push('is-valid');
  //   }
  //   if (Array.isArray(className)) {
  //     inputClasses.push(...className);
  //   } else if (className) {
  //     inputClasses.push(className);
  //   }

  //   const input = this.createElement({
  //     ...elementOptions,
  //     tag: 'input',
  //     className: inputClasses,
  //     attributes: {
  //       type,
  //       ...(placeholder && { placeholder }),
  //       ...(value && { value }),
  //       ...(required && { required: 'true' }),
  //       ...(disabled && { disabled: 'true' }),
  //       ...(readonly && { readonly: 'true' }),
  //       ...(min !== undefined && { min: min.toString() }),
  //       ...(max !== undefined && { max: max.toString() }),
  //       ...(step !== undefined && { step: step.toString() }),
  //       ...(pattern && { pattern })
  //     }
  //   }) as HTMLInputElement;

  //   container.appendChild(input);

  //   // 帮助文本
  //   if (help) {
  //     const helpElement = this.createElement({
  //       tag: 'div',
  //       className: 'form-text',
  //       textContent: help
  //     });
  //     container.appendChild(helpElement);
  //   }

  //   // 无效反馈
  //   if (invalid) {
  //     const invalidElement = this.createElement({
  //       tag: 'div',
  //       className: 'invalid-feedback',
  //       textContent: 'Please provide a valid value.'
  //     });
  //     container.appendChild(invalidElement);
  //   }

  //   return container;
  // }

  /**
   * 创建 Bootstrap 容器
   * @param options 容器配置选项
   * @returns 创建的容器元素
   */
  createContainer(options: ElementOptions & { fluid?: boolean } = {}): HTMLElement {
    const { fluid = false, className = [], ...elementOptions } = options;

    const containerClasses = fluid ? ['container-fluid'] : ['container'];
    if (Array.isArray(className)) {
      containerClasses.push(...className);
    } else if (className) {
      containerClasses.push(className);
    }

    return this.createElement({
      ...elementOptions,
      className: containerClasses
    });
  }

  /**
   * 创建 Bootstrap 行
   * @param options 行配置选项
   * @returns 创建的行元素
   */
  createRow(options: ElementOptions = {}): HTMLElement {
    return this.createElement({
      ...options,
      className: ['row', ...(Array.isArray(options.className) ? options.className : options.className ? [options.className] : [])]
    });
  }

  /**
   * 创建 Bootstrap 列
   * @param options 列配置选项
   * @returns 创建的列元素
   */
  createCol(options: ElementOptions & {
    xs?: number | 'auto';
    sm?: number | 'auto';
    md?: number | 'auto';
    lg?: number | 'auto';
    xl?: number | 'auto';
    xxl?: number | 'auto';
  } = {}): HTMLElement {
    const { xs, sm, md, lg, xl, xxl, className = [], ...elementOptions } = options;

    const colClasses = ['col'];
    if (xs !== undefined) colClasses.push(xs === 'auto' ? 'col-auto' : `col-${xs}`);
    if (sm !== undefined) colClasses.push(sm === 'auto' ? 'col-sm-auto' : `col-sm-${sm}`);
    if (md !== undefined) colClasses.push(md === 'auto' ? 'col-md-auto' : `col-md-${md}`);
    if (lg !== undefined) colClasses.push(lg === 'auto' ? 'col-lg-auto' : `col-lg-${lg}`);
    if (xl !== undefined) colClasses.push(xl === 'auto' ? 'col-xl-auto' : `col-xl-${xl}`);
    if (xxl !== undefined) colClasses.push(xxl === 'auto' ? 'col-xxl-auto' : `col-xxl-${xxl}`);

    if (Array.isArray(className)) {
      colClasses.push(...className);
    } else if (className) {
      colClasses.push(className);
    }

    return this.createElement({
      ...elementOptions,
      className: colClasses
    });
  }

  /**
   * 创建主题选择器
   * @param options 主题选择器配置选项
   * @returns 创建的主题选择器组件
   */
  createThemeSelector(options: ThemeSelectorOptions = {}): ThemeSelectorUI {
    return new ThemeSelectorUI(options);
  }

  /**
   * 创建下拉Tab组件
   * @param options 下拉Tab配置选项
   * @returns 创建的下拉Tab组件
   */
  createDropdownTab(options: DropdownTabOptions = { title: 'Dropdown Tab' }): DropdownTabUI {
    return new DropdownTabUI(options);
  }

  /**
   * 创建使用主题变量的按钮
   * @param options 按钮配置选项
   * @returns 创建的按钮元素
   */
  // createThemedButton(options: ButtonOptions = {}): HTMLButtonElement {
  //   const button = this.createButton(options);

  //   // 添加主题相关的样式类
  //   button.classList.add('vision-themed-button');


  //   return button;
  // }

  /**
   * 创建使用主题变量的输入框
   * @param options 输入框配置选项
   * @returns 创建的输入框元素
   */
  // createThemedInput(options: InputOptions = {}): HTMLElement {
  //   const input = this.createInput(options);

  //   // 添加主题相关的样式类
  //   input.classList.add('vision-themed-input');

  //   // 设置主题相关的内联样式
  //   input.style.setProperty('--input-bg', 'var(--vision-bg-color)');
  //   input.style.setProperty('--input-text', 'var(--vision-text-color)');
  //   input.style.setProperty('--input-border', 'var(--vision-border-color)');
  //   input.style.setProperty('--input-focus-border', 'var(--vision-primary-color)');
  //   input.style.setProperty('--input-placeholder', 'var(--vision-text-muted)');

  //   return input;
  // }

  /**
   * 创建使用主题变量的标签
   * @param options 标签配置选项
   * @returns 创建的标签元素
   */
  // createThemedLabel(options: LabelOptions = {}): HTMLLabelElement {
  //   const label = this.createLabel(options);

  //   // 添加主题相关的样式类
  //   label.classList.add('vision-themed-label');

  //   // 设置主题相关的内联样式
  //   label.style.setProperty('--label-text', 'var(--vision-text-color)');
  //   label.style.setProperty('--label-required', 'var(--vision-danger)');

  //   return label;
  // }

  /**
   * 创建使用主题变量的卡片
   * @param options 卡片配置选项
   * @returns 创建的卡片元素
   */
  // createThemedCard(options: CardOptions = {}): HTMLElement {
  //   const card = this.createCard(options);

  //   // 添加主题相关的样式类
  //   card.classList.add('vision-themed-card');

  //   // 设置主题相关的内联样式
  //   card.style.setProperty('--card-bg', 'var(--vision-bg-color)');
  //   card.style.setProperty('--card-border', 'var(--vision-border-color)');
  //   card.style.setProperty('--card-text', 'var(--vision-text-color)');
  //   card.style.setProperty('--card-shadow', 'var(--vision-shadow-sm)');

  //   return card;
  // }

  /**
   * 创建图标元素
   * @param options 图标配置选项
   * @returns 创建的图标 HTMLElement
   */
  createIcon(options: IconOptions): HTMLElement {
    const {
      iconName,
      size,
      color,
      spin = false,
      pulse = false,
      className = [],
      styles = {},
      ...elementOptions
    } = options;

    // 创建图标元素
    const icon = this.createElement({
      tag: 'i',
      className: ['icon', 'iconfont', `icon-${iconName}`, ...(Array.isArray(className) ? className : [className])],
      styles: {
        ...styles,
        ...(color && { color }),
        ...(typeof size === 'number' ? { fontSize: `${size}px` } : {}),
        ...(spin && { animation: 'spin 1s linear infinite' }),
        ...(pulse && { animation: 'pulse 1s ease-in-out infinite' })
      },
      ...elementOptions
    });

    // 添加尺寸类名
    if (typeof size === 'string') {
      icon.classList.add(`icon-${size}`);
    }

    // 添加动画类名
    if (spin) {
      icon.classList.add('icon-spin');
    }
    if (pulse) {
      icon.classList.add('icon-pulse');
    }

    return icon;
  }

  /**
   * 创建单选按钮组
   */
  createCheckButtonGroup(options: CheckButtonGroupOptions): CheckButtonGroupUI {
    return new CheckButtonGroupUI(options);
  }

  /**
   * 清理所有事件委托
   */
  cleanup(): void {
    this.eventDelegates.clear();
  }
}

// 导出默认实例
export const bootstrapUI = new BootstrapUI();
